import React, { useState, useRef, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { FaBars, FaTimes, FaPhone } from "react-icons/fa";
import "./Navbar.css";
import logo from "../assets/logo_itpsector2.png";

// React Icons
import { FaCarOn } from "react-icons/fa6";
import { FaMotorcycle } from "react-icons/fa";
import { TbZoomScan } from "react-icons/tb";
import { TbTruck } from "react-icons/tb";

const SERVICII = [
  {
    to: "/servicii/itp-autoturisme",
    icon: <FaCarOn />,
    label: "ITP Autoturisme",
    desc: "Inspecție Tehnică",
  },
  {
    to: "/servicii/itp-motociclete-atv",
    icon: <FaMotorcycle />,
    label: "ITP Motociclete și ATV",
    desc: "Inspecție periodică moto și ATV",
  },
  {
    to: "/servicii/scanare-computerizata",
    icon: <TbZoomScan />,
    label: "Scanare și diagnoză computerizată",
    desc: "Diagnoze Electronice",
  },
  {
    to: "/servicii/itp-vehicule-utilitare",
    icon: <TbTruck />,
    label: "ITP Vehicule Utilitare",
    desc: "Verificare autoutilitare",
  },
];

/* ─────────────────────────────────────────────────────────────
   DropdownMenu este acum o componentă INDEPENDENTĂ, definită
   în afara Navbar. Astfel fiecare instanță (desktop / mobil)
   are propriul ei state (dropdownOpen) și propriul ei ref,
   nu le mai împart între ele.
   ───────────────────────────────────────────────────────────── */
function DropdownMenu({ items, isActive, closeParent }) {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  const location = useLocation();

  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // închide dropdown-ul când se schimbă ruta (după navigare)
  useEffect(() => {
    setDropdownOpen(false);
  }, [location.pathname]);

  const isServiciiActive = items.some((s) => isActive(s.to));

  return (
    <div className="dropdown" ref={dropdownRef}>
      <button
        className={`dropdown__trigger${dropdownOpen ? " open" : ""}${
          isServiciiActive ? " active" : ""
        }`}
        onClick={() => setDropdownOpen((p) => !p)}
        aria-expanded={dropdownOpen}
        aria-haspopup="true"
      >
        Servicii
        <svg
          className="dropdown__chevron"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>

      <div className={`dropdown__menu${dropdownOpen ? " open" : ""}`} role="menu">
        {items.map((s) => (
          <Link
            key={s.to}
            to={s.to}
            className="dropdown__item"
            role="menuitem"
            onClick={() => {
              setDropdownOpen(false);
              if (closeParent) closeParent();
            }}
          >
            <span className="dropdown__item-icon">{s.icon}</span>
            <span className="dropdown__item-label">
              <span>{s.label}</span>
              <small>{s.desc}</small>
            </span>
          </Link>
        ))}
      </div>
    </div>
  );
}

export default function Navbar() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = mobileOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [mobileOpen]);

  const isActive = (p) => location.pathname === p;

  return (
    <>
      {/* Overlay mobil */}
      <div
        className={`nav-overlay${mobileOpen ? " active" : ""}`}
        onClick={() => setMobileOpen(false)}
        aria-hidden="true"
      />

      <header className="navbar">
        {/* Logo */}
        <Link to="/" className="navbar__logo" aria-label="Pagina principală">
          <img src={logo} alt="Logo ITP Sector 2" />
        </Link>

        {/* ── DESKTOP NAV ── */}
        <nav className="navbar__desktop" role="navigation" aria-label="Navigare desktop">
          <Link to="/" className={isActive("/") ? "active" : ""}>Acasă</Link>
          <Link to="/preturi" className={isActive("/preturi") ? "active" : ""}>Prețuri</Link>
          <DropdownMenu items={SERVICII} isActive={isActive} />
          <Link to="/contact" className={isActive("/contact") ? "active" : ""}>Contact</Link>
          <Link to="/bucuresti-sector2" className={isActive("/bucuresti-sector2") ? "active" : ""}>Galerie</Link>
          <a href="tel:+40726104358" className="cta-button">
            <FaPhone className="phone-icon" />
            SUNĂ ACUM
          </a>
        </nav>

        {/* ── MOBILE SLIDE PANEL ── */}
        <div
          className={`navbar__mobile-panel${mobileOpen ? " active" : ""}`}
          role="navigation"
          aria-label="Navigare mobil"
        >
          {/* Header cu gradient */}
          <div className="mobile-menu-header">
            <img src={logo} alt="Logo" className="mobile-menu-logo" />
            <button
              className="mobile-menu-close"
              onClick={() => setMobileOpen(false)}
              aria-label="Închide meniu"
            >
              <FaTimes />
            </button>
          </div>

          {/* Linkuri */}
          <div className="mobile-nav-body">
            <span className="mobile-nav-section-label">Navigare</span>
            <Link to="/" className={isActive("/") ? "active" : ""}>Acasă</Link>
            <Link to="/preturi" className={isActive("/preturi") ? "active" : ""}>Prețuri</Link>
            <DropdownMenu
              items={SERVICII}
              isActive={isActive}
              closeParent={() => setMobileOpen(false)}
            />
            <Link to="/contact" className={isActive("/contact") ? "active" : ""}>Contact</Link>
            <Link to="/bucuresti-sector2" className={isActive("/bucuresti-sector2") ? "active" : ""}>Galerie</Link>
          </div>

          {/* CTA jos */}
          <div className="mobile-cta-wrap">
            <a href="tel:+40726104358" className="cta-button">
              <FaPhone className="phone-icon" />
              SUNĂ ACUM
            </a>
          </div>
        </div>

        {/* Hamburger */}
        <button
          className="navbar__toggle"
          onClick={() => setMobileOpen((p) => !p)}
          aria-label={mobileOpen ? "Închide meniu" : "Deschide meniu"}
          aria-expanded={mobileOpen}
        >
          {mobileOpen ? <FaTimes /> : <FaBars />}
        </button>
      </header>
      <div className="offer-banner">
        <div className="offer-banner-track">
          <span>
            🔥 Oferte și reduceri de vară doar la ITP Sector 2 • Șoseaua Electronicii 24 •
            Fără programare • Program rapid • Sună acum: 0726 104 358
          </span>

          <span>
            🔥 Oferte și reduceri de vară doar la ITP Sector 2 • Șoseaua Electronicii 24 •
            Fără programare • Program rapid • Sună acum: 0726 104 358
          </span>
        </div>
      </div>
    </>
  );
}